/**
 * NoteMergePanel.jsx — Muhasebe FAZ 4.8, "Senet Birleştir" paneli.
 *
 * Uç: POST /admin/promissory-notes/merge
 * Body: { note_ids: [...], new_due_date: "YYYY-MM-DD", description }
 *
 * S-10 (KİLİTLİ KARAR) — vade ADMIN'den istenir, sistem HESAPLAMAZ. Bu
 * panelin 4 korkuluğu:
 *   1. `new_due_date` ZORUNLU alan; boşken submit engellenir (backend'in
 *      400 `senet_birlestirme_vade_zorunlu`'suna hiç gidilmez).
 *   2. Varsayılan olarak seçili senetlerin EN GEÇ vadesi ön-doldurulur
 *      (defaultMergeDueDate) — admin değiştirebilir; elle değiştirdikten
 *      sonra seçim değişse bile üzerine YAZILMAZ.
 *   3. Geçmiş tarih ENGELLENMEZ, yalnızca uyarılır (submit açık kalır);
 *      backend `due_date_in_past: true` dönerse aynı uyarı sonuç olarak da
 *      gösterilir.
 *   4. Biçim YYYY-AA-GG; `<input type="date">` + isValidDueDate ile
 *      doğrulanır (400 `senet_birlestirme_vade_gecersiz` önlenir).
 *
 * INV-5 seçim filtresi: yalnız AYNI fatura + AYNI öğrenci + `active` senetler
 * seçilebilir (mergeEligibleNotes) — kullanıcı 400 yemesin diye seçim
 * ekranında baştan kısıtlanır.
 */
import { useEffect, useMemo, useState } from 'react';
import { Merge } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useToast } from '../../../contexts/ToastContext';
import { useConfirmDialog } from '../../../components/admin';
import { fmtMoneyAccounting } from '../../../utils/money';
import { useMergePromissoryNotes } from '../../../api/queries/accounting';
import {
  ActionButton,
  Field,
  INPUT_CLASS,
  InlineAlert,
  NotePicker,
  TabIntro,
  isReconciliationError,
  noteErrorMessage,
} from './noteUiKit';
import {
  SPLIT_MIN_PARTS,
  defaultMergeDueDate,
  isActiveNote,
  isPastDueDate,
  isValidDueDate,
  mergeEligibleNotes,
  sumAmounts,
} from './promissoryMath';

export default function NoteMergePanel({ userId, notes }) {
  const { t, lang } = useLanguage();
  const toast = useToast();
  const confirm = useConfirmDialog();
  const mergeNotes = useMergePromissoryNotes(userId);

  const [selectedIds, setSelectedIds] = useState([]);
  const [dueDate, setDueDate] = useState('');
  const [dueDateTouched, setDueDateTouched] = useState(false);
  const [description, setDescription] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [stuck, setStuck] = useState(false);
  const [pastResult, setPastResult] = useState(false);

  const activeNotes = useMemo(() => (notes || []).filter((n) => isActiveNote(n.status)), [notes]);
  const anchor = activeNotes.find((n) => n.note_id === selectedIds[0]) || null;
  const eligible = useMemo(() => mergeEligibleNotes(activeNotes, anchor), [activeNotes, anchor]);
  const selectedNotes = useMemo(
    () => eligible.filter((n) => selectedIds.includes(n.note_id)),
    [eligible, selectedIds]
  );

  // S-10 madde 2 — varsayılan vade: seçili senetlerin EN GEÇ vadesi.
  // Admin alana bir kez dokunduysa (dueDateTouched) bir daha ezilmez.
  useEffect(() => {
    if (dueDateTouched) return;
    setDueDate(defaultMergeDueDate(selectedNotes));
  }, [selectedNotes, dueDateTouched]);

  const countValid = selectedIds.length >= SPLIT_MIN_PARTS;
  const dueMissing = !dueDate;
  const dueInvalid = !dueMissing && !isValidDueDate(dueDate);
  // S-10 madde 3: geçmiş tarih SUBMIT'İ ENGELLEMEZ, yalnızca uyarır.
  const duePast = !dueMissing && !dueInvalid && isPastDueDate(dueDate);
  const canSubmit = countValid && !dueMissing && !dueInvalid;

  const total = sumAmounts(selectedNotes.map((n) => n.amount));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (!canSubmit) return;

    const ok = await confirm({
      title: t('accounting.notes.merge.confirm_title'),
      description: `${t('accounting.notes.merge.confirm_description')} ${t('accounting.notes.irreversible')}`,
      variant: 'danger',
      confirmLabel: t('accounting.notes.confirm_continue'),
    });
    if (!ok) return;

    try {
      setStuck(false);
      const res = await mergeNotes.mutateAsync({
        note_ids: selectedIds,
        new_due_date: dueDate,
        description,
      });
      setPastResult(!!res?.due_date_in_past);
      toast.success(t('accounting.notes.merge.success'));
      setSelectedIds([]);
      setDescription('');
      setDueDateTouched(false);
      setSubmitted(false);
    } catch (err) {
      if (isReconciliationError(err)) setStuck(true);
      toast.error(noteErrorMessage(err, lang, t));
    }
  };

  return (
    <form className="space-y-4" onSubmit={onSubmit} noValidate>
      <TabIntro
        title={t('accounting.notes.merge.title')}
        description={t('accounting.notes.merge.description')}
      />

      {stuck && (
        <InlineAlert variant="danger" title={t('accounting.notes.reconcile_title')} testId="note-merge-reconcile-alert">
          {t('accounting.notes.reconcile_desc')}
        </InlineAlert>
      )}

      {pastResult && (
        <InlineAlert variant="warning" title={t('accounting.notes.merge.past_warning_title')} testId="note-merge-past-result">
          {t('accounting.notes.merge.past_warning')}
        </InlineAlert>
      )}

      <NotePicker
        id="note-merge-notes"
        testId="note-merge-picker"
        notes={eligible}
        value={selectedIds}
        onChange={setSelectedIds}
        multiple
        label={t('accounting.notes.merge.select_label')}
        required
        hint={t('accounting.notes.merge.select_hint')}
        emptyText={t('accounting.notes.merge.no_eligible')}
        error={submitted && !countValid ? t('accounting.notes.merge.min_error') : ''}
        lang={lang}
        t={t}
      />

      {selectedNotes.length > 0 && (
        <div className="flex items-center justify-between rounded-xl border-2 border-[var(--border-light)] px-3 py-2 text-xs font-bold">
          <span>{t('accounting.notes.merge.total_label')}</span>
          <span className="tabular-nums" data-testid="note-merge-total">{fmtMoneyAccounting(total, lang)}</span>
        </div>
      )}

      <Field
        id="note-merge-due"
        label={t('accounting.notes.merge.due_label')}
        required
        hint={t('accounting.notes.merge.due_hint')}
        error={
          submitted && dueMissing
            ? t('accounting.notes.merge.due_required')
            : dueInvalid
              ? t('accounting.notes.merge.due_invalid')
              : ''
        }
      >
        {({ id, describedBy }) => (
          <input
            id={id}
            type="date"
            className={INPUT_CLASS}
            value={dueDate}
            aria-required
            aria-invalid={(submitted && dueMissing) || dueInvalid ? true : undefined}
            aria-describedby={describedBy}
            onChange={(e) => { setDueDateTouched(true); setDueDate(e.target.value); }}
            data-testid="note-merge-due-input"
          />
        )}
      </Field>

      {duePast && (
        <InlineAlert variant="warning" title={t('accounting.notes.merge.past_warning_title')} testId="note-merge-past-warning">
          {t('accounting.notes.merge.past_warning')}
        </InlineAlert>
      )}

      <Field id="note-merge-description" label={t('accounting.notes.description_label')}>
        {({ id }) => (
          <input
            id={id}
            type="text"
            className={INPUT_CLASS}
            value={description}
            placeholder={t('accounting.notes.description_placeholder')}
            onChange={(e) => setDescription(e.target.value)}
            data-testid="note-merge-description-input"
          />
        )}
      </Field>

      <div className="flex justify-end">
        <ActionButton
          type="submit"
          variant="danger"
          icon={Merge}
          loading={mergeNotes.isPending}
          disabled={mergeNotes.isPending}
          testId="note-merge-submit"
        >
          {mergeNotes.isPending ? t('accounting.notes.submitting') : t('accounting.notes.merge.submit')}
        </ActionButton>
      </div>
    </form>
  );
}
